import qs from "qs";
import { IBoardInfo } from "@/domain/entities/board";
import TYPES from "@/ioc/identifiers";
import { SERVICE_UID } from "@/types";
import useGetAccessToken from "@/use/useGetAccessToken";
import useGetProtocolHost from "@/use/useGetProtocolHost";
import { inject, injectable } from "inversify";
import { Result } from "neverthrow";
import { HttpError } from "../shared/errors/httpError";
import { ParseError } from "../shared/errors/parseError";
import { IHttpService } from "../shared/httpService";
import {
  HttpBoardInfoResponse,
  IBoardInfoParser,
  IBoardRepository,
} from "./boardRepository.types";
import { IBaseParser, IBaseResponse } from "../shared/baseParser";
import { IResultData } from "@/domain/entities/shared/result";
import { ICustomCode } from "@/domain/entities/system/customCode.types";

@injectable()
export class BoardRepository implements IBoardRepository {
  @inject(TYPES.IBaseParser)
  private baseParser!: IBaseParser;

  @inject(TYPES.IBoardInfoParser)
  private boardInfoParser!: IBoardInfoParser;

  private httpService: IHttpService;

  constructor(@inject(TYPES.IHttpService) httpService: IHttpService) {
    this.httpService = httpService;
    this.httpService.initService(
      `${useGetProtocolHost()}/aibos/client/api/${
        process.env.VUE_APP_API_VERSION
      }/service/${SERVICE_UID}/board`
    );
  }

  async getBoardingHistory(
    boardDtmFrom: string,
    boardDtmTo: string,
    sortOrder?: string // 없다면 하드코딩된 값. 있다면 받아서 처리
  ): Promise<Result<IBoardInfo[], ParseError | HttpError>> {
    if (!sortOrder) {
      sortOrder = "realOnDtm desc";
    }
    const parseTo = (response: HttpBoardInfoResponse) => {
      return this.boardInfoParser.toDomain(response.data);
    };
    return await this.httpService.get<HttpBoardInfoResponse, IBoardInfo[]>(
      {
        url: `?boardDateFrom=${boardDtmFrom}&boardDateTo=${boardDtmTo}&sortOrder=${sortOrder}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async requestReport(
    boardUid: string,
    reportReason: ICustomCode
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: `/${boardUid}/report?reportCodeUid=${reportReason.uid}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async requestGetOffBus(
    boardUid: string,
    getOffBusStopId: string,
    getOffYn: boolean
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    const data = {
      offStationUid: getOffBusStopId,
      stopYn: getOffYn,
    };

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: `/${boardUid}/stop`,
        data: qs.stringify(data),
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
            "content-type": "application/x-www-form-urlencoded",
          },
        },
      },
      { parseTo }
    );
  }
}
